import PropTypes from 'prop-types';
import React, { useCallback, memo } from 'react';
import { connect } from 'react-redux';
import { List, Map } from 'immutable';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { handleGetHarvcoLeadSuggestion, handleGetClientLeadSuggestion } from '../../actions/popup';

import AddNewCEOProjectPopup from '../../components/Popup/AddNewCEOProjectPopup';
import { usePopupContext } from '../../contexts/PopupContext';

const handleGetSuggestionValue = suggestion => suggestion.text;

const handleRenderSuggestion = suggestion => <div>{suggestion.text}</div>;

const AddNewCEOProjectContainer = ({
  popup,
  handleGetHarvcoLeadSuggestion,
  handleGetClientLeadSuggestion,
}) => {
  const { closePopup, updatePopupFormData, startPopupLoading, endPopupLoading } = usePopupContext();

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      updatePopupFormData({
        [name]: value,
      });
    },
    [updatePopupFormData],
  );

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleCreate = useCallback(() => {
    const onCreate = popup.getIn(['props', 'onCreate'], () => {});
    const onSuccessResponse = popup.getIn(['props', 'onSuccessResponse'], () => {});

    onCreate(popup.get('formData'), {
      beforeStart: startPopupLoading,
      afterSuccess: () => {
        endPopupLoading();
        onSuccessResponse();
      },
      afterError: ({ responseBody }) => {
        endPopupLoading(responseBody);
      },
    });
  }, [popup, startPopupLoading, endPopupLoading]);

  const getHarvcoLeadSuccess = useCallback(
    ({ response }) => {
      updatePopupFormData({ harvcoLoading: false });
      updatePopupFormData({
        harvcoLead: {
          suggestions: response.data,
        },
      });
    },
    [updatePopupFormData],
  );

  const getHarvcoLeadFail = useCallback(() => {
    updatePopupFormData({ harvcoLoading: false });
  }, [updatePopupFormData]);

  const handleUpdateHarvcoLeadSuggestions = useCallback(
    update => {
      updatePopupFormData({
        harvcoLead: update,
      });
    },
    [updatePopupFormData],
  );

  const handleHarvcoLeadSuggestionFetch = useCallback(
    ({ value }) => {
      updatePopupFormData({ harvcoLoading: true });
      handleGetHarvcoLeadSuggestion(value, getHarvcoLeadSuccess, getHarvcoLeadFail);
    },
    [updatePopupFormData, handleGetHarvcoLeadSuggestion, getHarvcoLeadSuccess, getHarvcoLeadFail],
  );

  const handleHarvcoLeadSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      updatePopupFormData({
        harvcoLead: {
          text: suggestion.text,
          suggestions: [],
          selected: suggestion,
        },
      });
    },
    [updatePopupFormData],
  );

  const getClientLeadSuccess = useCallback(
    ({ response }) => {
      updatePopupFormData({ clientLoading: false });
      updatePopupFormData({
        clientLead: {
          suggestions: response.data,
        },
      });
    },
    [updatePopupFormData],
  );

  const getClientLeadFail = useCallback(() => {
    updatePopupFormData({ clientLoading: false });
  }, [updatePopupFormData]);

  const handleUpdateClientLeadSuggestions = useCallback(
    update => {
      updatePopupFormData({
        clientLead: update,
      });
    },
    [updatePopupFormData],
  );

  const handleClientLeadSuggestionFetch = useCallback(
    ({ value }) => {
      const buyerId = popup.getIn(['props', 'buyerId']);

      updatePopupFormData({ clientLoading: true });
      handleGetClientLeadSuggestion(buyerId, value, getClientLeadSuccess, getClientLeadFail);
    },
    [popup, updatePopupFormData, handleGetClientLeadSuggestion, getClientLeadSuccess, getClientLeadFail],
  );

  const handleClientLeadSuggestionSelected = useCallback(
    (event, { suggestion }) => {
      updatePopupFormData({
        clientLead: {
          text: suggestion.text,
          suggestions: [],
          selected: suggestion,
        },
      });
    },
    [updatePopupFormData],
  );

  const formData = popup.get('formData');

  const harvcoLeadSuggestionsProps = {
    inputProps: {
      className: 'form-control',
      id: 'harvcoLeadDropdown',
      name: 'harvcoLeadDropdown',
      label: 'HarvCo Lead',
      disabled: false,
      placeholder: '',
    },
    suggestions: popup.getIn(['formData', 'harvcoLead', 'suggestions'], new List()),
    onUpdateSuggestions: handleUpdateHarvcoLeadSuggestions,
    getSuggestion: handleHarvcoLeadSuggestionFetch,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleHarvcoLeadSuggestionSelected,
    text: popup.getIn(['formData', 'harvcoLead', 'text'], ''),
    loading: popup.getIn(['formData', 'harvcoLoading'], false),
  };

  const clientLeadSuggestionsProps = {
    inputProps: {
      className: 'form-control',
      id: 'clientLeadDropdown',
      name: 'clientLeadDropdown',
      label: 'Client Lead',
      disabled: false,
      placeholder: '',
    },
    suggestions: popup.getIn(['formData', 'clientLead', 'suggestions'], new List()),
    onUpdateSuggestions: handleUpdateClientLeadSuggestions,
    getSuggestion: handleClientLeadSuggestionFetch,
    getSuggestionValue: handleGetSuggestionValue,
    renderSuggestion: handleRenderSuggestion,
    onSuggestionSelected: handleClientLeadSuggestionSelected,
    text: popup.getIn(['formData', 'clientLead', 'text'], ''),
    loading: popup.getIn(['formData', 'clientLoading'], false),
  };

  return (
    <AddNewCEOProjectPopup
      clientLeadSuggestions={clientLeadSuggestionsProps}
      formData={formData}
      harvcoLeadSuggestions={harvcoLeadSuggestionsProps}
      isPopupFetching={popup.get('isPopupFetching')}
      isPopupFetchingError={popup.get('isPopupFetchingError')}
      onChange={handleChange}
      onClose={handleClose}
      onCreate={handleCreate}
    />
  );
};

AddNewCEOProjectContainer.propTypes = {
  popup: PropTypes.instanceOf(Map).isRequired,
};

export { AddNewCEOProjectContainer };
export default connect(
  (state, props) => ({
    ...props,
  }),
  {
    handleGetHarvcoLeadSuggestion,
    handleGetClientLeadSuggestion,
  },
  mergeProps,
  connectOptions,
)(memo(AddNewCEOProjectContainer));
